import { ragaTargetCents } from '../seed/target.js';
/**
 * Group a raga's ascending swara ladder into difficulty tiers:
 *  - tier 0: the anchor swaras themselves (S and/or P — always present when
 *    the raga has them; steady, unambiguous, easy to place by ear).
 *  - tier 1: other swaras NOT adjacent (in the ladder) to any anchor.
 *  - tier 2: swaras adjacent to an anchor — pitch-close to a stable degree
 *    and therefore the easiest to *confuse* with it, so the hardest to name.
 * If the raga has neither S nor P as a display symbol (shouldn't happen in
 * practice, but the seed schema doesn't forbid it), everything collapses
 * into a single tier so callers never crash on an empty pool.
 */
function difficultyTiers(ladder) {
    const anchors = ladder.filter((s) => s === 'S' || s === 'P');
    if (anchors.length === 0)
        return [[...ladder]];
    const anchorSet = new Set(anchors);
    const anchorIndices = ladder.reduce((acc, s, i) => {
        if (anchorSet.has(s))
            acc.push(i);
        return acc;
    }, []);
    const others = [];
    const nearAnchor = [];
    ladder.forEach((s, i) => {
        if (anchorSet.has(s))
            return;
        const adjacentToAnchor = anchorIndices.some((ai) => Math.abs(ai - i) === 1);
        (adjacentToAnchor ? nearAnchor : others).push(s);
    });
    return [anchors, others, nearAnchor].filter((tier) => tier.length > 0);
}
/** The candidate target pool for a given difficulty level: level 1 = tier 0 only, growing with level. */
function poolForLevel(ladder, level) {
    const tiers = difficultyTiers(ladder);
    const upTo = Math.max(1, Math.min(Math.floor(level), tiers.length));
    return tiers.slice(0, upTo).flat();
}
/**
 * A "name that swara" question for a single raga: pick a target swara (biased
 * by `level` toward anchor swaras at level 1, widening to include pitch-close
 * swaras at higher levels), and offer the raga's full swara set as options.
 * Pure: deterministic for a given (raga, level, rng-sequence).
 */
export function swaraQuestion(raga, level, rng) {
    const cents = ragaTargetCents(raga);
    // Ascending pitch ladder: sort swara_variants keys by their actual cents.
    // `Object.keys` insertion order is NOT reliable here — bhashanga/vakra
    // janyas (e.g. Bilahari: swaras ['S','R','G','P','D','m','N'] in ragas.ts)
    // list swaras in a scale-mnemonic order that isn't monotonic in pitch (m =
    // 498c comes after D = 884c), so sorting by cents is the only correct way
    // to derive true ascending neighbours. Object keys are already unique, so
    // this sort also supersedes any separate dedupe step.
    const ladder = Object.keys(raga.swara_variants).sort((a, b) => cents[a] - cents[b]);
    const safeLevel = Number.isFinite(level) ? level : 1; // guard against a NaN/Infinity caller bug
    const candidates = poolForLevel(ladder, safeLevel);
    const targetSwara = candidates[Math.floor(rng() * candidates.length)];
    const idx = ladder.indexOf(targetSwara);
    const hintNeighbours = {};
    if (idx > 0)
        hintNeighbours.lower = ladder[idx - 1];
    if (idx < ladder.length - 1)
        hintNeighbours.higher = ladder[idx + 1];
    return {
        targetSwara,
        targetCents: cents[targetSwara],
        options: ladder,
        hintNeighbours,
    };
}
/** In-place Fisher-Yates shuffle driven by the caller's seeded rng (deterministic per seed). */
function shuffle(items, rng) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
    }
    return out;
}
/** Count how many swarasthanas two ragas share (their actual pitch content, not display symbols). */
function swarasthanaOverlap(a, targetSwarasthanas) {
    let n = 0;
    for (const st of Object.values(a.swara_variants))
        if (targetSwarasthanas.has(st))
            n++;
    return n;
}
/**
 * A "name that raga" question: a short characteristic phrase (the target's
 * own arohana + avarohana, in its own display symbols) plus a multiple-choice
 * list of raga names. Distractors are drawn from `pool`, preferring ragas
 * that share swarasthanas with the target (musically confusable) over
 * unrelated ones, and are deduplicated by name. Degrades gracefully — fewer
 * distractors, never a crash or a duplicate option — when `pool` is small.
 * Pure: deterministic for a given (target, pool, rng-sequence).
 */
export function ragaQuestion(target, pool, rng) {
    const cents = ragaTargetCents(target);
    // Phrase: target's own arohana+avarohana, mapped from swarasthana back to
    // this raga's display symbol (the swara_variants mapping is injective by
    // construction, so every arohana/avarohana entry resolves to a symbol).
    const swarasthanaToSymbol = new Map();
    for (const [sym, st] of Object.entries(target.swara_variants))
        swarasthanaToSymbol.set(st, sym);
    const symbolsOf = (row) => row
        .map((st) => swarasthanaToSymbol.get(st))
        .filter((sym) => sym !== undefined);
    // `cents[swara]` is the within-octave value (S = 0), so the octave-top Sa that
    // ends the arohana / begins the avarohana would otherwise sound as the LOW Sa.
    // Give ABSOLUTE, octave-aware cents by tagging only that octave-top Sa (+1200):
    // in the arohana it's the tonic that ISN'T the opening one (index > 0); in the
    // avarohana it's the LEADING tonic (index 0). Every other note — including a
    // vakra raga's interior dips — keeps its plain within-octave cents, so a
    // zigzag arohana never gets falsely bumped an octave. (Matches how the raga
    // rail's `playableScale` resolves the octave.)
    const isTonic = (swara) => cents[swara] === 0; // S maps to 0 cents
    const arohana = symbolsOf(target.arohana).map((swara, i) => ({
        swara,
        cents: isTonic(swara) && i > 0 ? cents[swara] + 1200 : cents[swara],
    }));
    const avaSymbols = symbolsOf(target.avarohana);
    const avarohana = avaSymbols.map((swara, i) => ({
        swara,
        cents: isTonic(swara) && i === 0 ? cents[swara] + 1200 : cents[swara],
    }));
    const phrase = [...arohana, ...avarohana];
    // Distractor candidates: pool minus the target, deduped by name.
    const seenNames = new Set([target.name]);
    const candidates = [];
    for (const r of pool) {
        if (seenNames.has(r.name))
            continue;
        seenNames.add(r.name);
        candidates.push(r);
    }
    const targetSwarasthanas = new Set(Object.values(target.swara_variants));
    const ranked = candidates
        .map((r, i) => ({ r, score: swarasthanaOverlap(r, targetSwarasthanas), i }))
        .sort((a, b) => b.score - a.score || a.i - b.i) // highest overlap first; stable tie-break on pool order
        .map((x) => x.r);
    const desiredDistractors = ranked.length >= 3 ? (rng() < 0.5 ? 2 : 3) : Math.min(2, ranked.length);
    const distractors = ranked.slice(0, desiredDistractors);
    const options = shuffle([target.name, ...distractors.map((r) => r.name)], rng);
    return { targetRagaName: target.name, phrase, options };
}
//# sourceMappingURL=questions.js.map